/**
 * API client — thin wrapper over fetch.
 * All endpoints are behind /api/* on the same origin (proxied via CloudFront).
 * Admin routes use the Supabase access token from the current session.
 */
import { supabase } from "./supabase";

const BASE = "/api";

async function authHeaders(): Promise<HeadersInit> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

async function authRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const auth = await authHeaders();
  return request<T>(path, {
    ...init,
    headers: { ...auth, ...(init.headers || {}) },
  });
}

// ── Types ────────────────────────────────────────────────────────────
export type LinkMode = "public" | "nominative";

export interface LinkPublic {
  link_id: string;
  text: string;
  max_duration_sec: number;
  mode: LinkMode;
  recipient_name?: string;
  briefing_video_url?: string;
  is_avatar_link?: boolean;
}

export interface LinkAdmin {
  link_id: string;
  title: string;
  text: string;
  max_duration_sec: number;
  mode: LinkMode;
  recipient_name?: string;
  recipient_email?: string;
  token?: string;
  created_at: string;
  expires_at?: string;
  url: string;
  has_briefing: boolean;
  briefing_video_s3_key?: string;
  video_count: number;
  avatar_session_id?: string;
}

export interface CreateLinkRequest {
  text: string;
  mode: LinkMode;
  title?: string;
  recipient_name?: string;
  recipient_email?: string;
  max_duration_sec: number;
  expires_in_days?: number;
  briefing_video_s3_key?: string;
}

export interface BriefingSummary {
  s3_key: string;
  last_used_at: string;
  preview_url: string;
}

export interface InitUploadRequest {
  link_id: string;
  token?: string;
  mime_type: string;
  recorder_name?: string;
  recorder_email?: string;
}

export interface InitUploadResponse {
  video_id: string;
  upload_url: string;
  s3_key: string;
}

export interface InitBriefingUploadRequest {
  mime_type: string;
}

export interface InitBriefingUploadResponse {
  briefing_id: string;
  upload_url: string;
  s3_key: string;
}

export interface Video {
  video_id: string;
  link_id: string;
  link_title?: string;
  s3_key: string;
  status: string;
  mime_type: string;
  size_bytes?: number;
  duration_sec?: number;
  recorder_name?: string;
  recorder_email?: string;
  uploaded_at: string;
}

export interface FormatInfo {
  ready: boolean;
  download_url?: string;
  size_bytes?: number;
}

export interface VideoDetail {
  video_id: string;
  link_id: string;
  link_title?: string;
  uploaded_at: string;
  preview_url: string;
  filename_base: string;
  status: string;
  avatar_session_id?: string;
  duration_sec?: number;
  webm: FormatInfo;
  mp4: FormatInfo;
  mov: FormatInfo;
  convert_error?: string;
}

export interface BestofItem {
  slug: string;
  order: number;
  title: string;
  url: string;
}

// ── Public ───────────────────────────────────────────────────────────
export function getLink(linkId: string, token?: string): Promise<LinkPublic> {
  const qs = token ? `?token=${encodeURIComponent(token)}` : "";
  return request<LinkPublic>(`/links/${linkId}${qs}`);
}

export function getBestof(): Promise<BestofItem[]> {
  return request<BestofItem[]>(`/bestof`);
}

export function initVideoUpload(
  req: InitUploadRequest
): Promise<InitUploadResponse> {
  return request<InitUploadResponse>(`/videos/init`, {
    method: "POST",
    body: JSON.stringify(req),
  });
}

// ── Admin (requires Supabase session) ────────────────────────────────
// Login / signup / logout / password reset are handled by `supabase.auth.*`
// directly in the components — no need for dedicated API calls here.

export function listVideos(
  titleFilter?: string,
  limit?: number
): Promise<Video[]> {
  const params = new URLSearchParams();
  if (titleFilter) params.set("title", titleFilter);
  if (limit != null) params.set("limit", String(limit));
  const qs = params.toString();
  return authRequest<Video[]>(`/admin/videos${qs ? `?${qs}` : ""}`);
}

export function getVideoDetail(videoId: string): Promise<VideoDetail> {
  return authRequest<VideoDetail>(`/admin/videos/${videoId}/detail`);
}

export function listLinks(
  limit = 10,
  offset = 0,
): Promise<LinkAdmin[]> {
  return authRequest<LinkAdmin[]>(
    `/admin/links?limit=${limit}&offset=${offset}`,
  );
}

export function createLink(req: CreateLinkRequest): Promise<LinkAdmin> {
  return authRequest<LinkAdmin>(`/admin/links`, {
    method: "POST",
    body: JSON.stringify(req),
  });
}

export function initBriefingUpload(
  mimeType: string
): Promise<InitBriefingUploadResponse> {
  return authRequest<InitBriefingUploadResponse>(`/admin/briefing-videos/init`, {
    method: "POST",
    body: JSON.stringify({ mime_type: mimeType }),
  });
}

export function listBriefings(): Promise<BriefingSummary[]> {
  return authRequest<BriefingSummary[]>(`/admin/briefings`);
}

// ── CRM ───────────────────────────────────────────────────────────────
export interface CrmScoreCard {
  label: string;
  pct: number;
  verdict: "good" | "mid" | "bad";
}

export interface CrmTodaySummary {
  touchpoint: CrmScoreCard;
  pipeline: CrmScoreCard;
  due_actions: number;
  sidebar_badges: Record<string, number>;
}

export function fetchCrmTodaySummary(): Promise<CrmTodaySummary> {
  return authRequest<CrmTodaySummary>(`/crm/today-summary`);
}

export type CrmLeadStatus = "new" | "active" | "exited";

export interface CrmLead {
  lead_id: string;
  first_name: string;
  last_name?: string | null;
  email?: string | null;
  company?: string | null;
  status: CrmLeadStatus;
  next_step_at?: string | null;
  next_step_label?: string | null;
  notes?: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
}

export interface CrmLeadCreateBody {
  first_name: string;
  last_name?: string | null;
  email?: string | null;
  company?: string | null;
  status?: CrmLeadStatus;
  next_step_at?: string | null;
  next_step_label?: string | null;
  notes?: string | null;
}

export function listCrmLeads(params?: { status?: CrmLeadStatus }): Promise<CrmLead[]> {
  const q = params?.status ? `?status=${encodeURIComponent(params.status)}` : "";
  return authRequest<CrmLead[]>(`/crm/leads${q}`);
}

export function createCrmLead(body: CrmLeadCreateBody): Promise<CrmLead> {
  return authRequest<CrmLead>(`/crm/leads`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function getCrmLead(leadId: string): Promise<CrmLead> {
  return authRequest<CrmLead>(`/crm/leads/${encodeURIComponent(leadId)}`);
}

export function updateCrmLead(
  leadId: string,
  body: Partial<CrmLeadCreateBody>
): Promise<CrmLead> {
  return authRequest<CrmLead>(`/crm/leads/${encodeURIComponent(leadId)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function listCrmActions(): Promise<{ items: CrmLead[] }> {
  return authRequest<{ items: CrmLead[] }>(`/crm/actions`);
}

export interface CrmTraceItem {
  pk: string;
  sk: string;
  entity_type?: string;
  trace_id?: string;
  op?: string;
  admin_email?: string;
  created_at?: string;
  meta?: unknown;
}

export function listCrmTraces(params?: { limit?: number }): Promise<{ items: CrmTraceItem[] }> {
  const q =
    params?.limit != null ? `?limit=${encodeURIComponent(String(params.limit))}` : "";
  return authRequest<{ items: CrmTraceItem[] }>(`/crm/traces${q}`);
}

// ── Avatar ───────────────────────────────────────────────────────────
export interface AvatarCatalogPerson {
  id: string;
  name: string;
  meta: string;
  hue: number;
  image_url: string;
}

export interface AvatarCatalog {
  persons: AvatarCatalogPerson[];
  backgrounds: Record<string, { name: string; meta: string }>;
  voices: Record<string, { name: string; meta: string }>;
  language_options: { code: string; label: string }[];
  sample_driving_video_url: string | null;
}

export function fetchAvatarCatalog(): Promise<AvatarCatalog> {
  return authRequest<AvatarCatalog>("/avatar/catalog");
}

/** ElevenLabs preview MP3 (requires admin session). */
export async function fetchVoicePreviewMp3(voiceId: string): Promise<Blob> {
  const auth = await authHeaders();
  const res = await fetch(
    `${BASE}/avatar/voice-preview/${encodeURIComponent(voiceId)}`,
    { headers: { ...auth } }
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
  return res.blob();
}

export interface CreateAvatarSessionBody {
  person_id: string;
  bg_id: string;
  voice_id: string;
  langs: string[];
  script_text: string;
  script_by_lang?: Record<string, string>;
  /** Use an existing admin video as driving motion (no new recording). */
  reuse_video_id?: string;
  /**
   * Test/bypass mode: skip the Kling motion_control step and feed the driving
   * video straight to ElevenLabs+Sync.so. Validates TTS + lipsync without
   * depending on PiAPI. Final video keeps the original face.
   */
  skip_kling?: boolean;
}

export interface AvatarSessionCreated {
  session_id: string;
  link_id: string;
  recording_url: string;
  status: string;
  share_path: string;
}

export interface AvatarPipelineStepRow {
  step: string;
  label: string;
  status: "pending" | "running" | "done" | "failed" | string;
  duration_sec: number | null;
  elapsed_sec: number | null;
  eta_hint_sec: number | null;
}

export interface AvatarSessionDetail {
  session_id: string;
  link_id: string;
  recording_url: string;
  status: string;
  person_id: string;
  bg_id: string;
  voice_id: string;
  langs: string[];
  lang: string;
  reuse_video_id: string | null;
  pipeline_step: string | null;
  pipeline_error: string | null;
  pipeline_timeline?: AvatarPipelineStepRow[];
  final_download_url: string | null;
  final_download_urls: Record<string, string> | null;
}

export function createAvatarSession(
  body: CreateAvatarSessionBody
): Promise<AvatarSessionCreated> {
  return authRequest<AvatarSessionCreated>(`/avatar/sessions`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface TranscribeVideoResponse {
  transcript: string;
  duration_ms: number;
  cached: boolean;
}

export function transcribeVideoAudio(
  videoId: string
): Promise<TranscribeVideoResponse> {
  return authRequest<TranscribeVideoResponse>(
    `/avatar/transcribe-video/${videoId}`,
    { method: "POST" }
  );
}

export function getAvatarSession(
  sessionId: string
): Promise<AvatarSessionDetail> {
  return authRequest<AvatarSessionDetail>(`/avatar/sessions/${sessionId}`);
}

export function enqueueAvatarPipeline(
  sessionId: string
): Promise<AvatarSessionDetail> {
  return authRequest<AvatarSessionDetail>(
    `/avatar/sessions/${sessionId}/enqueue`,
    { method: "POST" }
  );
}

// ── Helper: upload a file to a pre-signed PUT URL ────────────────────
export async function uploadToPresignedUrl(
  url: string,
  file: Blob,
  contentType: string,
  onProgress?: (pct: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
    };
    xhr.onerror = () => reject(new Error("Upload network error"));
    xhr.send(file);
  });
}
